// ═══════════════════════════════════════════════════════════════
// Plain Language Task Parser
// Path: src/utils/parseTaskDirective.js
//
// Parses natural language directives into structured task objects.
// Pure JS — no external NLP dependencies. Designed so the internals
// can be swapped for an LLM call later without changing the interface.
//
// Usage:
//   const result = parseTaskDirective("follow up with Sarah next Tuesday", members);
//   // → { title, category, priority, dueDate, memberId, notes }
// ═══════════════════════════════════════════════════════════════

// ── Date Parsing ────────────────────────────────────────────────

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const DAY_ABBREVS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const MONTH_NAMES = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];
const MONTH_ABBREVS = [
  "jan", "feb", "mar", "apr", "may", "jun",
  "jul", "aug", "sep", "oct", "nov", "dec",
];

const toDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const getNextDayOfWeek = (dayIndex) => {
  const today = getToday();
  const current = today.getDay();
  let diff = dayIndex - current;
  if (diff <= 0) diff += 7;
  const result = new Date(today);
  result.setDate(result.getDate() + diff);
  return result;
};

/**
 * Extract a date from the input string. Returns { date: "YYYY-MM-DD", matched: "the matched text" } or null.
 */
const extractDate = (input) => {
  const lower = input.toLowerCase();
  const today = getToday();

  // "today"
  if (/\btoday\b/.test(lower)) {
    return { date: toDateStr(today), matched: lower.match(/\btoday\b/)[0] };
  }

  // "tomorrow"
  if (/\btomorrow\b/.test(lower)) {
    const d = new Date(today);
    d.setDate(d.getDate() + 1);
    return { date: toDateStr(d), matched: lower.match(/\btomorrow\b/)[0] };
  }

  // "end of week" / "end of the week" / "eow"
  const eowMatch = lower.match(/\b(end of (?:the )?week|eow)\b/);
  if (eowMatch) {
    const d = new Date(today);
    const dayOfWeek = d.getDay();
    const daysToFriday = dayOfWeek <= 5 ? 5 - dayOfWeek : 5 + (7 - dayOfWeek);
    d.setDate(d.getDate() + daysToFriday);
    return { date: toDateStr(d), matched: eowMatch[0] };
  }

  // "in N day(s)" / "in N week(s)" / "in N month(s)"
  const inNMatch = lower.match(/\bin\s+(\d+)\s+(day|week|month)s?\b/);
  if (inNMatch) {
    const n = parseInt(inNMatch[1], 10);
    const unit = inNMatch[2];
    const d = new Date(today);
    if (unit === "day") d.setDate(d.getDate() + n);
    else if (unit === "week") d.setDate(d.getDate() + n * 7);
    else if (unit === "month") d.setMonth(d.getMonth() + n);
    return { date: toDateStr(d), matched: inNMatch[0] };
  }

  // "next week" (next Monday)
  if (/\bnext week\b/.test(lower)) {
    return { date: toDateStr(getNextDayOfWeek(1)), matched: lower.match(/\bnext week\b/)[0] };
  }

  // "next <dayname>" e.g. "next tuesday"
  const nextDayMatch = lower.match(new RegExp(`\\bnext\\s+(${DAY_NAMES.join("|")})\\b`));
  if (nextDayMatch) {
    const dayIdx = DAY_NAMES.indexOf(nextDayMatch[1]);
    // "next X" means the occurrence in the NEXT week (7+ days out)
    const d = getNextDayOfWeek(dayIdx);
    const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
    if (diff < 7) d.setDate(d.getDate() + 7);
    return { date: toDateStr(d), matched: nextDayMatch[0] };
  }

  // "this <dayname>" e.g. "this friday"
  const thisDayMatch = lower.match(new RegExp(`\\bthis\\s+(${DAY_NAMES.join("|")})\\b`));
  if (thisDayMatch) {
    const dayIdx = DAY_NAMES.indexOf(thisDayMatch[1]);
    return { date: toDateStr(getNextDayOfWeek(dayIdx)), matched: thisDayMatch[0] };
  }

  // Bare day name: "tuesday", "friday" — means next occurrence
  const bareDayMatch = lower.match(new RegExp(`\\b(on\\s+)?(${DAY_NAMES.join("|")})\\b`));
  if (bareDayMatch) {
    const dayIdx = DAY_NAMES.indexOf(bareDayMatch[2]);
    return { date: toDateStr(getNextDayOfWeek(dayIdx)), matched: bareDayMatch[0] };
  }

  // "month day" e.g. "january 15", "jan 15"
  const allMonths = [...MONTH_NAMES, ...MONTH_ABBREVS].join("|");
  const monthDayMatch = lower.match(new RegExp(`\\b(${allMonths})\\s+(\\d{1,2})\\b`));
  if (monthDayMatch) {
    const monthName = monthDayMatch[1];
    let monthIdx = MONTH_NAMES.indexOf(monthName);
    if (monthIdx === -1) monthIdx = MONTH_ABBREVS.indexOf(monthName);
    const day = parseInt(monthDayMatch[2], 10);
    const d = new Date(today.getFullYear(), monthIdx, day);
    if (d < today) d.setFullYear(d.getFullYear() + 1);
    return { date: toDateStr(d), matched: monthDayMatch[0] };
  }

  return null;
};

// ── Priority Detection ──────────────────────────────────────────

const PRIORITY_PATTERNS = {
  high: /\b(urgent|asap|high priority|important|critical|rush)\b/i,
  low: /\b(low priority|when you can|eventually|no rush|whenever)\b/i,
};

const extractPriority = (input) => {
  if (PRIORITY_PATTERNS.high.test(input)) {
    return { priority: "high", matched: input.match(PRIORITY_PATTERNS.high)[0] };
  }
  if (PRIORITY_PATTERNS.low.test(input)) {
    return { priority: "low", matched: input.match(PRIORITY_PATTERNS.low)[0] };
  }
  return { priority: "normal", matched: null };
};

// ── Category Detection ──────────────────────────────────────────

const CATEGORY_PATTERNS = [
  { id: "follow_up", pattern: /\b(follow up|follow-up|followup|call|phone|reach out|check in|check-in|touch base)\b/i },
  { id: "proposal", pattern: /\b(proposal|propose|draft proposal|submit proposal)\b/i },
  { id: "acknowledgment", pattern: /\b(acknowledge|acknowledgment|acknowledgement|receipt)\b/i },
  { id: "thank", pattern: /\b(thank you|thank-you|thankyou|thanks|thank)\b/i },
  { id: "report", pattern: /\b(report|impact report|prepare report)\b/i },
  { id: "renewal", pattern: /\b(renew|renewal|re-new)\b/i },
  { id: "meeting", pattern: /\b(meet|meeting|schedule meeting|set up meeting|coffee|lunch|sit down)\b/i },
];

const extractCategory = (input) => {
  for (const cat of CATEGORY_PATTERNS) {
    if (cat.pattern.test(input)) {
      return { category: cat.id, matched: input.match(cat.pattern)[0] };
    }
  }
  return { category: "general", matched: null };
};

// ── Donor Matching ──────────────────────────────────────────────

/**
 * Try to find a member referenced in the input string.
 * Strategy: check for "firstName lastName" matches first, then first name only.
 * Returns { memberId, memberName, matched } or null.
 */
const extractDonor = (input, members) => {
  if (!members || members.length === 0) return null;

  const lower = input.toLowerCase();

  // Build searchable list
  const searchable = members.map((m) => {
    const first = (m.firstName || "").trim().toLowerCase();
    const last = (m.lastName || "").trim().toLowerCase();
    const full = `${first} ${last}`.trim();
    const displayName = `${m.firstName || ""} ${m.lastName || ""}`.trim();
    return { id: m.id, first, last, full, displayName };
  }).filter((m) => m.full.length > 0);

  // Pass 1: Full name match (most specific)
  for (const member of searchable) {
    if (member.full.length >= 3 && lower.includes(member.full)) {
      return { memberId: member.id, memberName: member.displayName, matched: member.full };
    }
  }

  // Pass 2: Last name match (only if unique among members)
  const lastNameCounts = {};
  for (const member of searchable) {
    if (member.last.length >= 3) {
      lastNameCounts[member.last] = (lastNameCounts[member.last] || 0) + 1;
    }
  }
  for (const member of searchable) {
    if (member.last.length >= 3 && lastNameCounts[member.last] === 1) {
      // Check it appears as a standalone word
      const regex = new RegExp(`\\b${escapeRegex(member.last)}\\b`, "i");
      if (regex.test(input)) {
        return { memberId: member.id, memberName: member.displayName, matched: member.last };
      }
    }
  }

  // Pass 3: First name match (only if unique and >= 3 chars to avoid false positives)
  const firstNameCounts = {};
  for (const member of searchable) {
    if (member.first.length >= 3) {
      firstNameCounts[member.first] = (firstNameCounts[member.first] || 0) + 1;
    }
  }
  for (const member of searchable) {
    if (member.first.length >= 3 && firstNameCounts[member.first] === 1) {
      const regex = new RegExp(`\\b${escapeRegex(member.first)}\\b`, "i");
      if (regex.test(input)) {
        return { memberId: member.id, memberName: member.displayName, matched: member.first };
      }
    }
  }

  return null;
};

const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// ── Title Cleaning ──────────────────────────────────────────────

const FILLER_PATTERNS = [
  /^(remind me to|i need to|i want to|please|can you|could you)\s+/i,
  /\s*(by|before|due|due by)\s+$/i,
];

const cleanTitle = (input, removals) => {
  let title = input;

  // Remove matched fragments (dates, priority markers, etc.)
  for (const removal of removals) {
    if (removal) {
      title = title.replace(new RegExp(escapeRegex(removal), "i"), " ");
    }
  }

  // Remove filler phrases
  for (const pattern of FILLER_PATTERNS) {
    title = title.replace(pattern, "");
  }

  // Remove dangling prepositions at end: "with", "for", "by", "on", "to"
  title = title.replace(/\s+(with|for|by|on|to|before|due)\s*$/i, "");

  // Clean up whitespace
  title = title.replace(/\s+/g, " ").trim();

  // Capitalize first letter
  if (title.length > 0) {
    title = title.charAt(0).toUpperCase() + title.slice(1);
  }

  return title;
};

// ── Main Parser ─────────────────────────────────────────────────

/**
 * Parse a plain language directive into a structured task object.
 *
 * @param {string} input — The natural language directive
 * @param {Array} members — The members array from AppContext
 * @returns {{ title, category, priority, dueDate, memberId, memberName, notes }}
 */
export const parseTaskDirective = (input, members = []) => {
  if (!input || !input.trim()) {
    return null;
  }

  const trimmed = input.trim();

  // Extract each component
  const dateResult = extractDate(trimmed);
  const priorityResult = extractPriority(trimmed);
  const categoryResult = extractCategory(trimmed);
  const donorResult = extractDonor(trimmed, members);

  // Build list of matched fragments to remove from title
  const removals = [
    dateResult?.matched,
    priorityResult?.matched,
    donorResult?.matched,
  ];

  // Build clean title
  const title = cleanTitle(trimmed, removals);

  return {
    title: title || trimmed,
    category: categoryResult.category,
    priority: priorityResult.priority,
    dueDate: dateResult?.date || null,
    memberId: donorResult?.memberId || null,
    memberName: donorResult?.memberName || null,
  };
};
